<template>
  <div class="draft-order__container">
    <div class="feedback">
      <transition name="fade" @enter="enterSuccessFeedback">
        <FeedbackSuccess
          v-if="showableSuccessFeedback"
          :message="noticeMessage"
        />
      </transition>
    </div>
    <div class="feedback">
      <transition name="fade" @enter="enterErrorFeedback">
        <FeedbackError
          v-if="showableErrorFeedback"
          :message="feedbackErrorMessage"
        />
      </transition>
    </div>
    <div v-if="!isApp" class="draft-order__header">
      <PageHeadlineWithFourSteps
        :steps="steps"
        :active-step="activeStep"
        title="決済方法の選択"
      />
    </div>
    <div class="draft-order__selected-gift">
      <div class="draft-order__selected-gift__container">
        <div v-if="hasErrorMessages" class="draft-order__error-message">
          <div class="box-list--alert">
            <div
              v-for="(message, index) in boxListErrorMessages"
              :key="index"
              class="box-list--alert__message"
            >
              <!-- div 直下 に message を置くと、フォーマットの関係で タグとメッセージの間に改行とインデントが入り、表示に反映されてしまうので、タグとメッセージが同じ行になるようにしている -->
              <p>{{ message }}</p>
            </div>
          </div>
        </div>
        <h3 class="draft-order__selected-gift__container__title">
          選択中のギフト
        </h3>
        <TicketConnectedMultiple
          v-for="itemSku in itemSkus"
          :key="itemSku.id"
          :image-url="itemSku.imageUrl"
          :expired-date="itemSku.expiredDate"
          :item-name="itemSku.itemName"
          :brand-name="itemSku.brandName"
          :is-including-tax="itemSku.isIncludingTax"
          :item-sku-name="
            itemSku.hasMultipleItemSkus ? itemSku.itemSkuName : ''
          "
          :ticket-count="itemSku.quantity"
          :price="itemSku.price"
        />
        <div class="draft-order__selected-gift__container__price">
          <div
            v-if="displayableDiscountPrice"
            class="draft-order__discount-price"
          >
            <span class="draft-order__discount-price__title">
              ギフトコード値引き分
            </span>
            <span class="draft-order__discount-price__value">
              - ¥{{ discountPrice.toLocaleString() }}
            </span>
          </div>
          <div class="draft-order__payment-price">
            <span class="draft-order__payment-price__title">決済合計</span>
            <span
              v-if="isIncludingTaxItems"
              class="draft-order__payment-price__including-tax"
            >
              （税込）
            </span>
            <span class="draft-order__payment-price__value">
              ¥{{ paymentPrice.toLocaleString() }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="paymentPrice > 0" class="draft-order__payment-method">
      <div
        class="draft-order__payment-method__container"
        :class="{ 'is-empty-payment-method': isEmptyPaymentMehod }"
      >
        <h3 class="draft-order__payment-method__container__title">
          決済方法
        </h3>
        <a
          href="/payment_methods/input"
          class="draft-order__payment-method__container__link text-link--sub tl-link-chevron-forward--right"
        >
          <div
            v-if="isEmptyPaymentMehod"
            class="draft-order__payment-method__add"
          >
            選択する
          </div>
          <div
            v-else-if="isCreditCardPayment"
            class="draft-order__payment-method__credit"
          >
            <CreditCardBrand
              v-if="creditCardInfo.card_brand"
              class="draft-order__payment-method__credit-card-brand"
              :brand-type="creditCardInfo.card_brand"
              :icon-width="40"
              :icon-height="27"
            />
            <span class="draft-order__payment-method__credit-card-number">
              {{ creditCardInfo.cc_number }}
            </span>
            <span class="draft-order__payment-method__credit-card-expired-at">
              有効期限
              {{ convertCreditCardExpiredAt(creditCardInfo.cc_expiration) }}
            </span>
          </div>
          <div v-else class="draft-order__payment-method__name">
            {{ paymentMethod }}
          </div>
        </a>
      </div>
      <div v-if="isPayPay" class="notice">
        <NoticeBox>
          途中でPayPayアプリや画面を閉じると決済が正常に完了しない場合がございます。
          <b>購入完了画面が表示されたことを確認するまで</b>必ずお待ちください
        </NoticeBox>
      </div>
      <div v-if="isMerpay" class="notice">
        <NoticeBox>
          メルペイをご利用の際は、事前にメルカリアプリでのログインをお願いします。
        </NoticeBox>
      </div>
      <div
        v-if="isRakutenPayDisabled"
        class="draft-order__payment-method__message"
      >
        ※楽天ペイは決済合計が100円以上の決済にのみご利用いただけます。
      </div>
    </div>
    <div class="draft-order__gift-code">
      <div class="draft-order__gift-code__title-area">
        <span class="body-bold">
          ギフトコード（お持ちの方のみ）
        </span>
        <a
          :href="`/draft_orders/${draftOrderForm.draft_order_id}/gift_codes`"
          class="text-link--sub tl-link-chevron-forward--right"
        >
          {{ isNotEmptyGiftCodeSerial ? '変更する' : '利用する' }}
        </a>
      </div>
      <div class="notice">
        <NoticeBox>
          ギフトコードは1回の決済で全額適用され、分割してご利用いただくことはできません。
        </NoticeBox>
      </div>
      <div
        v-if="isNotEmptyGiftCodeSerial"
        class="draft-order__gift-code__form-area"
      >
        <form
          method="post"
          :action="
            `/draft_orders/${draftOrderForm.draft_order_id}/clear_gift_code`
          "
          class="close-circle-text-form"
        >
          <input
            :value="giftCodeSerial.serial"
            type="text"
            class="close-circle-text-field--inputted"
            readonly
          />
          <button class="close-circle" type="submit" />
          <input type="hidden" name="authenticity_token" :value="csrfToken" />
          <input type="submit" class="hidden-submit" />
        </form>
      </div>
    </div>
    <form
      action="/draft_orders/confirm"
      method="post"
      class="draft-order__pay-form"
    >
      <div class="draft-order__pay-form__container">
        <input type="hidden" name="authenticity_token" :value="csrfToken" />
        <div
          v-if="draftOrderForm.has_alcohol"
          class="draft-order__pay-form__container__over-twenty"
        >
          <div
            class="draft-order__pay-form__container__over-twenty__checkbox"
            :class="{
              checkbox: !isOverTwenty,
              'checkbox--checked': isOverTwenty
            }"
            @click="onClickOverTwenty"
          >
            <label
              for="over_twenty_check"
              class="draft-order__pay-form__container__over-twenty-check-label"
              @click="onClickOverTwenty"
            >
              あなたは20歳以上ですか？
            </label>
            <input
              id="over_twenty_check"
              v-model="isOverTwenty"
              type="checkbox"
              name="draft_order_form[over_twenty_check]"
            />
          </div>
          <p class="minimal">
            20歳未満での酒類の購入や飲酒は日本の法律により固く禁じられています
          </p>
          <p class="minimal">
            確認をいただきチェックをいれると決済に進めます
          </p>
        </div>
        <!-- prettier-ignore -->
        <p class="draft-order__pay-form__container__about-cancel">
          キャンセルについては<span
            class="draft-order__pay-form__container__about-cancel-modal-link"
            @click="onClickAboutCancelLink"
          >こちら</span>をご確認ください
        </p>
        <input
          type="hidden"
          name="draft_order_form[draft_order_id]"
          :value="draftOrderForm.draft_order_id"
        />
        <input
          type="hidden"
          name="draft_order_form[gift_card_session_id]"
          :value="draftOrderForm.gift_card_session_id"
        />
        <input
          type="hidden"
          name="draft_order_form[gift_code_serial_id]"
          :value="draftOrderForm.gift_code_serial_id"
        />
        <input
          type="hidden"
          name="draft_order_form[draft_order_updated_at]"
          :value="draftOrderForm.draft_order_updated_at"
        />
        <input
          type="hidden"
          name="draft_order_form[gift_card_session_updated_at]"
          :value="draftOrderForm.gift_card_session_updated_at"
        />
        <input
          type="hidden"
          name="draft_order_form[gift_code_serial_updated_at]"
          :value="draftOrderForm.gift_code_serial_updated_at"
        />
        <input
          type="hidden"
          name="draft_order_form[has_alcohol]"
          :value="draftOrderForm.has_alcohol"
        />
        <button
          v-if="!isAmazonpayPayment"
          id="order-submit-button"
          type="submit"
          class="btn btn--primary"
          data-disable-with="注文内容を確認する"
          :disabled="disableSubmitButton || null"
        >
          注文内容を確認する
        </button>
        <div v-else id="AmazonPayButton" class="amazonpay-button"></div>
        <div
          v-if="!isApp"
          class="draft-order__pay-form__container__back-create-gift-link"
        >
          <a v-if="!draftOrderForm.is_personal_use" href="/gift_card/edit">
            ギフトカード作成に戻る
          </a>
          <a
            v-else
            :href="backToItemUrl(`${draftOrderForm.personal_use_item_id}`)"
          >
            ギフト詳細に戻る
          </a>
        </div>
      </div>
    </form>
    <TextModalWithoutPrimary
      v-if="showableAboutCancelModel"
      title-text="キャンセルについて"
      caption-text="ほげほげ"
      cancel-button-text="閉じる"
      @clicked-modal-cover="closeCancelAboutModal"
      @clicked-cancel="closeCancelAboutModal"
    >
      <ul class="draft-order__about-cancel-modal-list">
        <li>
          お申込み確定後のお申込み内容の変更・キャンセルは、以下の場合を除き、承ることができませんので、あらかじめご了承ください。
        </li>
        <li>
          ・サービス停止により、電子チケットと商品との交換が不能となった場合
        </li>
        <li>
          ・システムの障害により、電子チケットと商品との交換が不能になった場合
        </li>
        <li>
          ・対象商品等の販売終了または対象店舗の閉鎖により対象商品等との交換が不能になった場合
        </li>
      </ul>
    </TextModalWithoutPrimary>
  </div>
</template>
<script>
import axios from 'axios'
import dayjs from 'dayjs'
import NoticeBox from '../../atoms/boxes/NoticeBox.vue'
import FeedbackSuccess from '../../atoms/feedbacks/feedback-success.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import PageHeadlineWithFourSteps from '../../molecules/headlines/PageHeadlineWithFourSteps.vue'
import TicketConnectedMultiple from '../../atoms/tickets/TicketConnectedMultiple.vue'
import CreditCardBrand from '../../molecules/credit_cards/CreditCardBrand.vue'
import TextModalWithoutPrimary from '../../molecules/modals/text-modal-without-primary.vue'
export default {
  components: {
    TextModalWithoutPrimary,
    FeedbackSuccess,
    FeedbackError,
    PageHeadlineWithFourSteps,
    TicketConnectedMultiple,
    NoticeBox,
    CreditCardBrand
  },
  props: {
    noticeMessage: {
      type: String,
      required: false,
      default: ''
    },
    feedbackErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    boxListErrorMessages: {
      validator(value) {
        return value === '' || Array.isArray(value)
      },
      default: () => []
    },
    itemSkuContentData: {
      type: Object,
      required: true
    },
    discountPrice: {
      type: Number,
      required: false,
      default: 0
    },
    paymentPrice: {
      type: Number,
      required: false,
      default: 0
    },
    paymentMethod: {
      type: String,
      required: false,
      default: ''
    },
    isAmazonpayPayment: {
      type: Boolean,
      required: true
    },
    isCreditCardPayment: {
      type: Boolean,
      required: true
    },
    creditCardInfo: {
      type: Object,
      required: false,
      default: () => ({})
    },
    giftCodeSerial: {
      type: Object,
      required: false,
      default: () => ({})
    },
    draftOrderForm: {
      type: Object,
      required: true
    },
    needToPay: {
      type: Boolean,
      required: true
    },
    isApp: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      showableSuccessFeedback: false,
      showableErrorFeedback: false,
      activeStep: 3,
      steps: ['作成', 'プレビュー', '確認', '完了'],
      itemSkus: [],
      csrfToken: '',
      isOverTwenty: false,
      showableAboutCancelModel: false
    }
  },
  computed: {
    hasErrorMessages() {
      return this.boxListErrorMessages && this.boxListErrorMessages.length !== 0
    },
    displayableDiscountPrice() {
      return this.discountPrice > 0
    },
    isEmptyPaymentMehod() {
      return this.paymentMethod.length === 0
    },
    isNotEmptyGiftCodeSerial() {
      return (
        this.giftCodeSerial !== null &&
        Object.keys(this.giftCodeSerial).length !== 0 &&
        this.giftCodeSerial.serial
      )
    },
    disableSubmitButton() {
      return (
        (this.draftOrderForm.has_alcohol && !this.isOverTwenty) ||
        (this.needToPay && this.isEmptyPaymentMehod) ||
        this.isRakutenPayDisabled
      )
    },
    isIncludingTaxItems() {
      return this.itemSkus.some(sku => sku.isIncludingTax)
    },
    isRakutenPayDisabled() {
      return this.paymentMethod === '楽天ペイ' && this.paymentPrice < 100
    },
    isPayPay() {
      return this.paymentMethod === 'PayPay'
    },
    isMerpay() {
      return this.paymentMethod === 'メルペイ'
    },
    // amazonPayで支払方法の選択後のリダイレクトURLの生成
    amazonPayCheckoutReviewReturnUrl() {
      const params = new URLSearchParams()
      params.set('draft_order_id', this.draftOrderForm.draft_order_id)
      params.set(
        'gift_card_session_id',
        this.draftOrderForm.gift_card_session_id
      )
      if (this.draftOrderForm.gift_code_serial_id) {
        params.set(
          'gift_code_serial_id',
          this.draftOrderForm.gift_code_serial_id
        )
      }
      params.set(
        'draft_order_updated_at',
        this.draftOrderForm.draft_order_updated_at
      )
      params.set(
        'gift_card_session_updated_at',
        this.draftOrderForm.gift_card_session_updated_at
      )
      if (this.draftOrderForm.gift_code_serial_updated_at) {
        params.set(
          'gift_code_serial_updated_at',
          this.draftOrderForm.gift_code_serial_updated_at
        )
      }
      params.set('has_alcohol', this.draftOrderForm.has_alcohol)
      params.set('over_twenty_check', this.isOverTwenty)
      return `https://${
        location.host
      }/draft_orders/confirm_amazonpay?${params.toString()}`
    }
  },
  mounted() {
    this.showableSuccessFeedback = this.noticeMessage.length !== 0
    this.showableErrorFeedback = this.feedbackErrorMessage.length !== 0
    this.itemSkus = this.itemSkuContentData.data.map(
      itemSku => itemSku.attributes
    )
    this.csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute('content')

    // AmazonPayの場合はAmazonPayのボタンを生成
    if (this.isAmazonpayPayment) {
      this.setupAmazonPayButton()
    }
  },
  methods: {
    enterSuccessFeedback() {
      setTimeout(() => {
        this.showableSuccessFeedback = false
      }, 3000)
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    onClickOverTwenty() {
      this.isOverTwenty = !this.isOverTwenty
    },
    convertCreditCardExpiredAt(date) {
      return dayjs(date).format('MM/YY')
    },
    onClickAboutCancelLink() {
      this.showableAboutCancelModel = true
    },
    closeCancelAboutModal() {
      this.showableAboutCancelModel = false
    },
    backToItemUrl(personalUseItemId) {
      if (personalUseItemId === 'null') {
        return '/items'
      }
      return `/items/${personalUseItemId}`
    },
    async getAmazonPayButtonSetting(checkoutReviewReturnUrl) {
      const response = await axios.get(
        `/api/v1/amazonpay/button_setting?checkout_review_return_url=${encodeURIComponent(
          checkoutReviewReturnUrl
        )}`
      )
      return {
        merchantId: response.data.merchant_id,
        storeId: response.data.store_id,
        payload: response.data.payload,
        sandbox: response.data.sandbox,
        signature: response.data.signature,
        algorithm: response.data.algorithm,
        publicKeyId: response.data.public_key_id
      }
    },
    async setupAmazonPayButton() {
      const amazonPayButtonSetting = await this.getAmazonPayButtonSetting(
        this.amazonPayCheckoutReviewReturnUrl
      )

      // eslint-disable-next-line no-undef
      const amazonPayButton = amazon.Pay.renderButton('#AmazonPayButton', {
        merchantId: amazonPayButtonSetting.merchantId,
        ledgerCurrency: 'JPY',
        sandbox: amazonPayButtonSetting.sandbox,
        checkoutLanguage: 'ja_JP',
        productType: 'PayOnly',
        placement: 'Cart'
      })

      // signatureの生成にリダイレクトURL(amazonPayCheckoutReviewReturnUrl)を利用するため、
      // URLのパラメータが変化すること(現状isOverTwentyのみ)を踏まえてクリック時にsignatureを設定する
      amazonPayButton.onClick(async () => {
        if (this.disableSubmitButton) return
        const amazonPayButtonSetting = await this.getAmazonPayButtonSetting(
          this.amazonPayCheckoutReviewReturnUrl
        )

        amazonPayButton.initCheckout({
          createCheckoutSessionConfig: {
            payloadJSON: amazonPayButtonSetting.payload,
            signature: amazonPayButtonSetting.signature,
            algorithm: amazonPayButtonSetting.algorithm,
            publicKeyId: amazonPayButtonSetting.publicKeyId
          }
        })
      })
    }
  }
}
</script>
<style lang="scss" scoped>
.draft-order {
  &__payment-method {
    &__message {
      @extend %minimal;
      max-width: 343px;
      margin: auto;
      display: flex;
      justify-content: left;
      padding: 0 16px 16px 16px;
    }

    &__credit {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &__credit-card-brand {
      margin-right: 8px;
    }
  }
}
.notice {
  padding: 0 16px 16px;
  margin: auto;
  max-width: 343px;
}
.box-list--alert__message {
  white-space: pre-line;
}
.amazonpay-button {
  height: 56px;
}
</style>
